'use client'

import { motion } from 'framer-motion'
import {
  Briefcase,
  Building,
  Calendar,
  Check,
  CreditCard,
  Edit,
  FileText,
  Globe,
  Mail,
  MapPin,
  Monitor,
  Phone,
  Star,
  User,
} from 'lucide-react'

import { UserProfile, WorkshopPreference } from '../../lib/workshop-api'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card } from '../ui/card'

interface ReviewSelectionProps {
  userEmail: string
  userProfile: UserProfile | null
  selectedWorkshops: WorkshopPreference[]
  onConfirm: () => void
  onEditInfo: () => void
  onEditPreferences: () => void
}

export function ReviewSelection({
  userEmail,
  userProfile,
  selectedWorkshops,
  onConfirm,
  onEditInfo,
  onEditPreferences,
}: ReviewSelectionProps) {
  const sortedWorkshops = [...selectedWorkshops].sort((a, b) => a.preference - b.preference)

  const getTotalEstimatedPrice = () => {
    // This would need actual pricing data from the workshops
    // For now, we'll use placeholder pricing
    return sortedWorkshops.reduce((total, workshop) => {
      const basePrice = workshop.format === 'in-person' ? 500 : 300 // Placeholder
      return total + basePrice
    }, 0)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <Card className="p-6 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-shuttle-navy/10 dark:bg-shuttle-navy/20">
          <Check className="h-8 w-8 text-shuttle-navy dark:text-shuttle-red" />
        </div>
        <h2 className="text-2xl font-semibold text-shuttle-navy dark:text-white">
          Review Your Registration
        </h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Please review your information and workshop selection before submitting.
        </p>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Personal Information */}
        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center space-x-2 text-lg font-semibold">
              <User className="h-5 w-5 text-shuttle-red" />
              <span>Personal Information</span>
            </h3>
            <Button variant="ghost" size="sm" onClick={onEditInfo}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </div>

          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Mail className="mt-1 h-4 w-4 text-gray-500" />
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Email</div>
                <div className="font-medium">{userEmail}</div>
              </div>
            </div>

            {userProfile?.name && (
              <div className="flex items-start space-x-3">
                <User className="mt-1 h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Name</div>
                  <div className="font-medium">{userProfile.name}</div>
                </div>
              </div>
            )}

            {userProfile?.phone && (
              <div className="flex items-start space-x-3">
                <Phone className="mt-1 h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Phone</div>
                  <div className="font-medium">{userProfile.phone}</div>
                </div>
              </div>
            )}

            {userProfile?.company && (
              <div className="flex items-start space-x-3">
                <Building className="mt-1 h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Company</div>
                  <div className="font-medium">{userProfile.company}</div>
                </div>
              </div>
            )}

            {userProfile?.jobTitle && (
              <div className="flex items-start space-x-3">
                <Briefcase className="mt-1 h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Job Title</div>
                  <div className="font-medium">{userProfile.jobTitle}</div>
                </div>
              </div>
            )}

            {(userProfile?.city || userProfile?.country) && (
              <div className="flex items-start space-x-3">
                <MapPin className="mt-1 h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Location</div>
                  <div className="font-medium">
                    {[userProfile?.city, userProfile?.country].filter(Boolean).join(', ')}
                  </div>
                </div>
              </div>
            )}

            {userProfile?.paymentPreference && (
              <div className="flex items-start space-x-3">
                {userProfile.paymentPreference === 'stripe' ? (
                  <CreditCard className="mt-1 h-4 w-4 text-gray-500" />
                ) : (
                  <FileText className="mt-1 h-4 w-4 text-gray-500" />
                )}
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Payment Preference</div>
                  <div className="font-medium capitalize">
                    {userProfile.paymentPreference === 'stripe'
                      ? 'Credit/Debit Card'
                      : 'Invoice (Xero)'}
                  </div>
                </div>
              </div>
            )}

            {userProfile?.marketingConsent && (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                ✓ Opted in to marketing communications
              </div>
            )}
          </div>
        </Card>

        {/* Workshop Selection */}
        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center space-x-2 text-lg font-semibold">
              <Calendar className="h-5 w-5 text-shuttle-red" />
              <span>Workshop Preferences</span>
            </h3>
            <Button variant="ghost" size="sm" onClick={onEditPreferences}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </div>

          <div className="space-y-4">
            {sortedWorkshops.map((workshop, index) => (
              <div
                key={workshop.workshopInstanceId}
                className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800"
              >
                <div className="flex items-start justify-between">
                  <div className="flex flex-1 items-start space-x-3">
                    <Badge
                      variant="outline"
                      className="flex h-8 w-8 items-center justify-center rounded-full"
                    >
                      {workshop.preference}
                    </Badge>
                    <div className="flex-1">
                      <div className="font-medium">{workshop.title}</div>
                      <div className="mt-1 flex items-center space-x-2">
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                          {workshop.format === 'in-person' ? (
                            <MapPin className="mr-1 h-3 w-3" />
                          ) : (
                            <Monitor className="mr-1 h-3 w-3" />
                          )}
                          <span className="capitalize">{workshop.format}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                          <Star className="mr-1 h-3 w-3 fill-yellow-400 text-yellow-400" />
                          Preference {workshop.preference}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {sortedWorkshops.length === 0 && (
              <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                No workshops selected
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Summary and Pricing */}
      <Card className="p-6">
        <h3 className="mb-4 text-lg font-semibold">Registration Summary</h3>

        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">
              Number of workshop preferences:
            </span>
            <span className="font-medium">{sortedWorkshops.length}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Payment method:</span>
            <span className="font-medium capitalize">
              {userProfile?.paymentPreference === 'stripe' ? 'Credit/Debit Card' : 'Invoice (Xero)'}
            </span>
          </div>

          {/* Estimated pricing note */}
          <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
            <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
              <h4 className="mb-2 font-medium text-blue-900 dark:text-blue-100">
                What happens next?
              </h4>
              <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
                <li>• We'll check availability for your preferred workshops</li>
                <li>• You'll be assigned to the highest preference workshop available</li>
                <li>• If your preferences aren't available, you can join the waitlist</li>
                <li>• Payment will be processed only after workshop assignment</li>
                <li>• You'll receive confirmation and workshop details via email</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>

      {/* Privacy and Terms */}
      <Card className="bg-gray-50 p-6 dark:bg-gray-800">
        <h4 className="mb-3 font-medium">Before you submit</h4>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>By submitting this registration, you acknowledge that:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Your information will be used to process your workshop registration</li>
            <li>
              You agree to our{' '}
              <a href="/terms" className="text-shuttle-red hover:underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" className="text-shuttle-red hover:underline">
                Privacy Policy
              </a>
            </li>
            <li>Workshop availability is subject to confirmation</li>
            <li>Cancellation policies apply as outlined in the workshop terms</li>
          </ul>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <div className="flex space-x-4">
          <Button variant="ghost" onClick={onEditInfo}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Information
          </Button>
          <Button variant="ghost" onClick={onEditPreferences}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Preferences
          </Button>
        </div>

        <Button
          onClick={onConfirm}
          size="lg"
          disabled={sortedWorkshops.length === 0}
          className="bg-shuttle-red hover:bg-shuttle-red/90"
        >
          Submit Registration
        </Button>
      </div>
    </motion.div>
  )
}
